import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { BookingThreadList, NegotiationThread } from "@/components/bookings/NegotiationThread";
import { BookingJobSummary } from "@/components/bookings/BookingJobSummary";
import { BookingActions, BookingStatusPanels } from "@/components/bookings/BookingActions";
import { Inbox } from "lucide-react";

export default function Bookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(null);

  const load = useCallback(async (selectId) => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
      const targetId = selectId || params.get("id");
      if (targetId) {
        const found = data.find((b) => b.id === targetId);
        if (found) setSelected(found);
      } else if (data.length > 0) {
        setSelected((prev) => prev || data[0]);
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not load bookings"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (user?.role === "client") {
      api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
    }
  }, [user, selected?.status]);

  const respond = async (action, amount = null, note = "") => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${selected.id}/respond`, { action, amount, note });
      setSelected(data);
      await load(data.id);
      toast.success(action === "accept" ? "Offer accepted" : action === "decline" ? "Booking declined" : "Counter offer sent");
      return true;
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${selected.id}/pay`);
      setSelected({ ...data.booking, my_side: "client" });
      await load(data.booking.id);
      toast.success(`Payment complete — licence active until ${new Date(data.license.end_at).toLocaleDateString()}`);
    } catch (e) {
      toast.error(errMsg(e, "Payment failed"));
    } finally {
      setBusy(false);
    }
  };

  const selectBooking = (b) => {
    setSelected(b);
    setParams({ id: b.id });
  };

  if (loading) {
    return (
      <div className="page-container grid gap-6 py-10 lg:grid-cols-12">
        <Skeleton className="h-64 rounded-xl lg:col-span-4" />
        <Skeleton className="h-96 rounded-xl lg:col-span-8" />
      </div>
    );
  }

  const myTurn = selected && selected.awaiting === selected.my_side && ["pending", "countered"].includes(selected.status);
  const isClient = selected?.my_side === "client";
  const agreed = selected?.agreed_amount ?? selected?.current_offer?.amount;

  return (
    <div className="page-container py-8 sm:py-12">
      <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Bookings</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        {user?.role === "client" ? "Your booking requests and negotiations." : "Requests for your looks — you decide."}
      </p>

      {bookings.length === 0 ? (
        <div className="mt-12 rounded-xl border border-dashed p-12 text-center">
          <Inbox className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-3 font-display text-xl">No bookings yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {user?.role === "client" ? "Find a look you love and send your first request." : "When clients request your look, it appears here."}
          </p>
          {user?.role === "client" && (
            <Button className="mt-4 rounded-xl" onClick={() => navigate("/explore")}>Explore models</Button>
          )}
        </div>
      ) : (
        <div className="mt-8 grid gap-6 lg:grid-cols-12">
          <BookingThreadList bookings={bookings} selectedId={selected?.id} onSelect={selectBooking} />

          {selected && (
            <div data-testid="inbox-thread-detail" className="space-y-4 lg:col-span-8">
              <Card className="rounded-2xl">
                <CardContent className="space-y-4 p-5">
                  <BookingJobSummary booking={selected} />
                  <Separator />
                  <NegotiationThread thread={selected.thread} />
                  {myTurn && (
                    <BookingActions key={selected.id} booking={selected} isClient={isClient} busy={busy} onRespond={respond} />
                  )}
                  <BookingStatusPanels
                    booking={selected}
                    isClient={isClient}
                    myTurn={myTurn}
                    agreed={agreed}
                    wallet={wallet}
                    busy={busy}
                    onPay={pay}
                  />
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
